'use client';

import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import Icon from '@/components/ui/AppIcon';
import { scheduleCall } from '@/lib/cta';

interface FormData {
  name: string;
  email: string;
  company: string;
  projectType: string;
  description: string;
  website: string; // honeypot
}

const FinalCTASection = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    projectType: '',
    description: '',
    website: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const projectTypes = [
    { value: 'mobile', label: 'Mobile App' },
    { value: 'web', label: 'Web App' },
    { value: 'both', label: 'Web + Mobile' },
    { value: 'admin', label: 'Admin Panel' },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.projectType) {
      setError('Please select a project type.');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the security check.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          projectType: formData.projectType,
          description: formData.description,
          website: formData.website,
          turnstileToken,
        }),
      });

      const data = (await response.json().catch(() => ({}))) as { error?: string };

      if (!response.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        return;
      }

      setIsSubmitted(true);
      scheduleCall();
    } catch {
      setError('Something went wrong. Please try again.');
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="start-mvp" className="section-pad section-rule relative z-10 bg-background">
        <motion.div
          className="mx-auto max-w-lg text-center"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="mb-4 font-display text-3xl font-bold text-foreground">Thank you</h2>
          <p className="mb-8 text-base text-text-secondary">
            We&apos;ve received your request. Our team will reach out within 24 hours to schedule
            your strategy session.
          </p>
          <ul className="space-y-3 border-t border-border pt-6 text-left text-sm text-text-secondary">
            <li>Within 24 hours: Initial consultation call scheduled</li>
            <li>Day 1–2: Requirements gathering and project scoping</li>
            <li>Day 3: Kickoff and start of your 28-day MVP journey</li>
          </ul>
        </motion.div>
      </section>
    );
  }

  return (
    <section id="start-mvp" className="section-pad section-rule relative z-10 bg-background">
      <div className="mx-auto max-w-7xl">
        <motion.div
          className="mb-8 max-w-2xl sm:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <p className="mb-2 font-sans text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-[var(--brand-orange)] sm:mb-3 sm:text-xs">
            Let&apos;s build
          </p>
          <h2 className="mb-3 font-display text-2xl font-bold tracking-tight text-foreground sm:mb-4 sm:text-4xl lg:text-5xl">
            Ready to launch your MVP?
          </h2>
          <p className="text-sm leading-relaxed text-text-secondary sm:text-lg">
            Book a free strategy session and get a custom roadmap for your 28-day launch.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <motion.form
            onSubmit={handleSubmit}
            className="relative space-y-5 rounded-xl border border-border bg-card p-5 sm:p-8"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="font-display text-xl font-semibold text-foreground">
              Start your MVP journey
            </h3>

            {/* Honeypot — hidden from users, filled by bots */}
            <div
              className="absolute -left-[9999px] h-0 w-0 overflow-hidden"
              aria-hidden="true"
            >
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={formData.website}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground">
                Full name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                maxLength={100}
                value={formData.name}
                onChange={handleInputChange}
                className="w-full rounded-md border border-border bg-background px-4 py-3 text-base text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm"
                placeholder="Jane Founder"
              />
            </div>

            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground">
                Email address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                maxLength={254}
                value={formData.email}
                onChange={handleInputChange}
                className="w-full rounded-md border border-border bg-background px-4 py-3 text-base text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm"
                placeholder="jane@startup.com"
              />
            </div>

            <div>
              <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-foreground">
                Company name
              </label>
              <input
                type="text"
                id="company"
                name="company"
                maxLength={150}
                value={formData.company}
                onChange={handleInputChange}
                className="w-full rounded-md border border-border bg-background px-4 py-3 text-base text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm"
                placeholder="Your startup"
              />
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-foreground">Project type *</p>
              <div className="grid grid-cols-2 gap-2">
                {projectTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, projectType: type.value }))}
                    className={`border px-3 py-2.5 text-left text-sm transition-colors ${
                      formData.projectType === type.value
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border text-foreground hover:border-foreground'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label
                htmlFor="description"
                className="mb-1.5 block text-sm font-medium text-foreground"
              >
                Tell us about your idea
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                maxLength={5000}
                value={formData.description}
                onChange={handleInputChange}
                className="w-full rounded-md border border-border bg-background px-4 py-3 text-base text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm"
                placeholder="Describe your MVP idea, target audience, and key features..."
              />
            </div>

            {siteKey ? (
              <Turnstile
                ref={turnstileRef}
                siteKey={siteKey}
                onSuccess={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
                options={{ theme: 'light' }}
              />
            ) : null}

            {error ? (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-md bg-[var(--brand-orange)] py-3.5 text-sm font-semibold text-[var(--brand-ink)] transition-colors hover:bg-[#ffb01a] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? 'Sending…' : 'Book free strategy session'}
            </button>

            <p className="text-center text-xs text-text-secondary">
              By submitting, you agree to our privacy policy. We&apos;ll never share your
              information.
            </p>
          </motion.form>

          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {[
              {
                title: '100% risk-free guarantee',
                body: "If we don't deliver your MVP in 28 days, you get a full refund. No questions asked.",
              },
              {
                title: 'Your IP is protected',
                body: 'We sign NDAs before starting. You own 100% of the code, designs, and intellectual property.',
              },
              {
                title: 'Free strategy session',
                body: "No commitment required. We'll analyze your idea and show you exactly how we'll build it.",
              },
            ].map((item) => (
              <div key={item.title} className="border-t border-border pt-6">
                <h4 className="mb-2 font-display text-lg font-semibold text-foreground">
                  {item.title}
                </h4>
                <p className="text-sm leading-relaxed text-text-secondary">{item.body}</p>
              </div>
            ))}

            <div className="border border-border bg-muted/50 p-6">
              <h4 className="mb-2 font-display text-lg font-semibold text-foreground">
                Prefer to talk?
              </h4>
              <p className="mb-4 text-sm text-text-secondary">
                Schedule a call with our team to discuss your MVP requirements.
              </p>
              <button
                onClick={scheduleCall}
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              >
                Schedule a call
                <Icon name="ArrowRightIcon" size={14} />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTASection;
